import React from 'react';
import Rating5 from './questions/Rating5';
import ScaleRating from './questions/ScaleRating';
import SingleChoice from './questions/SingleChoice';
import MultipleChoice from './questions/MultipleChoice';
import TextAnswer from './questions/TextAnswer';
import { User, AlertCircle } from 'lucide-react';

const QuestionCard = ({
  step,
  answers,
  onAnswerChange,
  extraAnswers,
  onExtraTextChange,
  disabled,
  error
}) => {
  if (!step) return null;

  // Single Question Input Renderer
  const renderInputWidget = (qType, qOptions, qValue, onValChange, isMixed, subText, extraVal, onExtraChange) => {
    switch (qType) {
      case 'rating_5':
        return (
          <Rating5
            value={qValue}
            onChange={onValChange}
            disabled={disabled}
          />
        );
      case 'rating_10':
        return (
          <ScaleRating
            value={qValue}
            onChange={onValChange}
            min={1}
            max={10}
            disabled={disabled}
            type="rating_10"
          />
        );
      case 'nps':
        return (
          <ScaleRating
            value={qValue}
            onChange={onValChange}
            min={0}
            max={10}
            disabled={disabled}
            type="nps"
          />
        );
      case 'single_choice':
        return (
          <SingleChoice
            options={qOptions}
            value={qValue}
            onChange={onValChange}
            subQuestionText={isMixed ? subText : null}
            extraText={extraVal}
            onExtraTextChange={onExtraChange}
            disabled={disabled}
          />
        );
      case 'multiple_choice':
        return (
          <MultipleChoice
            options={qOptions}
            value={qValue}
            onChange={onValChange}
            subQuestionText={isMixed ? subText : null}
            extraText={extraVal}
            onExtraTextChange={onExtraChange}
            disabled={disabled}
          />
        );
      case 'text':
      default:
        return (
          <TextAnswer
            value={qValue || ''}
            onChange={onValChange}
            disabled={disabled}
          />
        );
    }
  };

  // Grouped Speaker Question View
  if (step.isGrouped) {
    return (
      <div className="card-survey animate-fadeIn w-full max-w-2xl mx-auto space-y-6">
        {/* Header Shared Title */}
        <div className="border-b border-slate-100 pb-4">
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-800 leading-snug">
            {step.text}
            {step.required && (
              <span className="text-adium-red ml-1" title="Obligatorio">*</span>
            )}
          </h2>
          <p className="text-xs text-slate-400 font-medium mt-1">
            Por favor, evalúa a cada uno de los conferencistas listados a continuación:
          </p>
        </div>

        {/* Speaker Voting Cards */}
        <div className="space-y-6">
          {step.questions.map((subQ, idx) => {
            const subAns = answers[subQ.id];
            const subExtra = extraAnswers[subQ.id];

            return (
              <div
                key={subQ.id || idx}
                className="p-5 sm:p-6 bg-slate-50 border border-slate-200/80 rounded-2xl transition-all duration-200 hover:border-blue-300 hover:shadow-md"
              >
                {/* Speaker Identity Card */}
                <div className="flex items-center gap-4 mb-4 pb-3 border-b border-slate-200/60">
                  <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-lg overflow-hidden flex-shrink-0 border-2 border-blue-200 shadow-sm">
                    {subQ.speaker_photo_url ? (
                      <img
                        src={subQ.speaker_photo_url}
                        alt={subQ.speaker_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-6 h-6 text-blue-700" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-slate-800">
                      {subQ.speaker_name || `Conferencista ${idx + 1}`}
                    </h3>
                    {subQ.speaker_profession && (
                      <p className="text-xs text-slate-500 font-medium">
                        {subQ.speaker_profession}
                      </p>
                    )}
                  </div>
                </div>

                {/* Speaker Rating Widget */}
                <div>
                  {renderInputWidget(
                    subQ.type,
                    subQ.options,
                    subAns,
                    (val) => onAnswerChange(subQ.id, val),
                    subQ.is_mixed,
                    subQ.sub_question_text,
                    subExtra,
                    (txt) => onExtraTextChange(subQ.id, txt)
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Error message */}
        {error && (
          <div className="mt-4 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-semibold flex items-center gap-2 animate-fadeIn">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  // Single Question View
  return (
    <div className="card-survey animate-fadeIn w-full max-w-2xl mx-auto">
      {/* Speaker Tag if attached */}
      {step.speaker_name && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full text-xs font-semibold mb-4 shadow-sm">
          <User className="w-3.5 h-3.5" />
          <span>Conferencista: {step.speaker_name}</span>
        </div>
      )}

      {/* Question Header */}
      <div className="mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-800 leading-snug">
          {step.text}
          {step.required ? (
            <span className="text-adium-red ml-1 text-base" title="Obligatorio">*</span>
          ) : null}
        </h2>
      </div>

      {/* Question Body */}
      <div className="my-4">
        {renderInputWidget(
          step.type,
          step.options,
          answers[step.id],
          (val) => onAnswerChange(step.id, val),
          step.is_mixed,
          step.sub_question_text,
          extraAnswers[step.id],
          (txt) => onExtraTextChange(step.id, txt)
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mt-4 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-semibold flex items-center gap-2 animate-fadeIn">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
